import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/registration_form.css";

// Helper to derive which levels are included from the chosen pass label.
// Works with current naming scheme: single level (Footprints/Milestones/Giant Steps) or
// combo names with parentheses e.g. "Reflections (Open + Advanced)", "Resolutions (Full Pack)".
function deriveLevels(passLabel = "") {
	const pass = passLabel.trim();
	if (!pass) return { open: false, advanced: false, invitational: false };
	// Extract content inside parentheses if present (e.g. "Reflections (Open + Advanced)")
	const m = pass.match(/\(([^)]+)\)/);
	const inner = m ? m[1] : null;
	const target = (inner || pass).toLowerCase();
	// Reuse the same broad heuristics as validation logic below so visibility matches requirements
	const wantsOpen =
		/(footprints|open)/i.test(target) || /reflections|resolutions|full pack/i.test(pass);
	const wantsAdvanced =
		/(milestones|advanced)/i.test(target) ||
		/reflections|recollections|resolutions|full pack/i.test(pass);
	const wantsInvitational =
		/(giant steps|invitational)/i.test(target) || /recollections|resolutions|full pack/i.test(pass);
	return { open: wantsOpen, advanced: wantsAdvanced, invitational: wantsInvitational };
}
// Restored clean component with scoped pricing grouping for CSS form only
export default function RegistrationForm({ formKey = "registration-26", cssAccessToken }) {
	const [schema, setSchema] = useState(null);
	const [form, setForm] = useState({});
	const [status, setStatus] = useState(null);
	const [promoInfo, setPromoInfo] = useState(null); // {code, spec, remaining}
	const [promoError, setPromoError] = useState(null); // store last promo error (only after manual check)
	const [promoFlash, setPromoFlash] = useState(null); // transient success message
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	// Static merch image mapping (dummy images in public/)
	const merchImages = {
		// Remaining merch (Kontogiorgis only)
		tshirt_konto: { src: "/merch/tshirt_front.png", alt: "Kontogiorgis vol.4 T-shirt" },
		tshirt_konto_back: {
			src: "/merch/tshirt_back.png",
			alt: "Kontogiorgis vol.4 T-shirt (back)"
		},
		tshirt_konto_f: { src: "/merch/tshirt_2_front.png", alt: "Kontogiorgaina T-shirt (front)" },
		tote_diy26: { src: "/merch/tote_diy26.png", alt: "DIY '26 tote bag" },
		tote_diy25: { src: "/merch/tote_diy25.png", alt: "DIY '25 tote bag" }
	};

	// Clear role selections that become irrelevant when pass changes (normal form)
	useEffect(() => {
		if (schema?.key !== "registration-26") return;
		setForm((prev) => {
			const pass = prev.pass || "";
			const needed = new Set();
			if (pass) {
				const lvls = deriveLevels(pass);
				if (lvls.open) needed.add("role_Open");
				if (lvls.advanced) needed.add("role_Advanced");
				if (lvls.invitational) needed.add("role_Invitational");
			}
			let changed = false;
			const next = { ...prev };
			["role_Open", "role_Advanced", "role_Invitational"].forEach((k) => {
				if (!needed.has(k) && next[k] != null && next[k] !== "") {
					delete next[k];
					changed = true;
				}
			});
			// Also clear legacy roleOther if no remaining role_* has value "Other"
			if (
				next.roleOther &&
				![next.role_Open, next.role_Advanced, next.role_Invitational].some((v) => v === "Other")
			) {
				delete next.roleOther;
				changed = true;
			}
			return changed ? next : prev;
		});
	}, [schema?.key, form.pass]);

	// Clear role selections for CSS class-selection form when related Lindy class unchecked
	useEffect(() => {
		if (schema?.key !== "registration-26-css") return;
		setForm((prev) => {
			const classes = prev.classes || [];
			const hasOpen = classes.some((c) => /Lindy Hop\s+Footprints/i.test(c));
			const hasAdv = classes.some((c) => /Lindy Hop\s+Milestones/i.test(c));
			const hasInv = classes.some((c) => /Lindy Hop\s+Giant Steps/i.test(c));
			let changed = false;
			const next = { ...prev };
			if (!hasOpen && next.role_Open) {
				delete next.role_Open;
				changed = true;
			}
			if (!hasAdv && next.role_Advanced) {
				delete next.role_Advanced;
				changed = true;
			}
			if (!hasInv && next.role_Invitational) {
				delete next.role_Invitational;
				changed = true;
			}
			if (
				next.roleOther &&
				![next.role_Open, next.role_Advanced, next.role_Invitational].some((v) => v === "Other")
			) {
				delete next.roleOther;
				changed = true;
			}
			return changed ? next : prev;
		});
	}, [schema?.key, form.classes]);

	// When capacity status updates, clear a selected pass that has become invalid (normal form only)
	useEffect(() => {
		if (schema?.key !== "registration-26" || !schema.capacityStatus || !form.pass) return;
		const lvls = deriveLevels(form.pass);
		const cs = schema.capacityStatus;
		const invalid =
			(lvls.open && cs.Open?.allFull) ||
			(lvls.advanced && cs.Advanced?.allFull) ||
			(lvls.invitational && cs.Invitational?.allFull);
		if (invalid) {
			setForm((prev) => ({ ...prev, pass: "" }));
		}
	}, [schema?.capacityStatus]);

	// Backend routes mounted under /festivalaki (use relative paths so same-origin works in production)
	const endpoint = `/festivalaki/${formKey}`;
	useEffect(() => {
		document.body.dataset.formKey = formKey;
		return () => {
			if (document.body.dataset.formKey === formKey) delete document.body.dataset.formKey;
		};
	}, [formKey]);

	useEffect(() => {
		const headers = {};
		if (formKey === "registration-26-css" && cssAccessToken)
			headers["x-css-access"] = cssAccessToken;
		fetch(endpoint, { headers })
			.then(async (r) => {
				const json = await r.json().catch(() => ({}));
				if (!r.ok) throw new Error(json.error || "Failed to load form");
				setSchema(json);
			})
			.catch((e) => setStatus({ error: e.message || "Failed to load form" }));
	}, [endpoint, formKey, cssAccessToken]);

	// Display helper: keep underlying schema values intact while adjusting visible labels.
	//  - 'Invitational' -> 'Advanced+'
	//  - Pass combo formatting: replace 'Open + Advanced' with 'Open - Advanced'
	//    and 'Advanced + Invitational' -> 'Advanced - Advanced+' (after Invitational replacement)
	//    and 'Advanced + Advanced+' -> 'Advanced - Advanced+'
	const displayLabel = (text) => {
		if (typeof text !== "string") return text;
		let out = text.replace(/\bInvitational\b/g, "Advanced+");
		out = out.replace(/Open\s*\+\s*Advanced/gi, "Open - Advanced");
		out = out.replace(/Advanced\s*\+\s*Advanced\+/gi, "Advanced - Advanced+");
		out = out.replace(/Advanced\s*\+\s*Advanced\b/gi, "Advanced - Advanced"); // fallback (unlikely)
		return out;
	};

	const onChange = (name, value, type) => {
		setForm((prev) => {
			if (type === "multiSelect") {
				const cur = new Set(prev[name] || []);
				if (cur.has(value)) cur.delete(value);
				else cur.add(value);
				return { ...prev, [name]: Array.from(cur) };
			}
			// Reset promo state if promoCode changes
			if (name === "promoCode") {
				return { ...prev, [name]: value };
			}
			return { ...prev, [name]: value };
		});
	};

	// Manual promo code validation handler
	const validatePromoManually = () => {
		if (schema?.key !== "registration-26") return;
		const raw = form.promoCode?.trim();
		setPromoError(null);
		setPromoInfo(null);
		setPromoFlash(null);
		if (!raw) return; // nothing to validate
		const params = new URLSearchParams({ code: raw });
		if (form.pass) params.set("pass", form.pass);
		fetch(`/festivalaki/registration-26/promo/validate?${params.toString()}`)
			.then((r) => r.json().then((j) => ({ ok: r.ok, body: j })))
			.then(({ ok, body }) => {
				if (!ok) throw body;
				setPromoInfo(body);
				setPromoFlash(
					`Promo code applied${body.remaining != null ? ` (remaining: ${body.remaining})` : ""}`
				);
			})
			.catch((err) => {
				setPromoInfo(null);
				if (err?.code === "INVALID_PROMO") setPromoError("Promo code not recognized");
				else if (err?.code === "EXPIRED_PROMO") setPromoError("Promo code usage limit reached");
				else if (err?.code === "PROMO_RESTRICTED")
					setPromoError(err.error || "Promo code restriction");
				else setPromoError("Promo code validation failed");
			});
	};

	useEffect(() => {
		if (!promoFlash) return;
		const t = setTimeout(() => setPromoFlash(null), 3500);
		return () => clearTimeout(t);
	}, [promoFlash]);

	const validate = () => {
		if (!schema) return null;
		// Dynamic per-level role requirement (normal form pass logic OR css form class selection)
		let requiredRoleFields = [];
		if (schema.key === "registration-26") {
			const pass = form.pass || "";
			// Pass labels now have canonical names (Reflections, Recollections, Resolutions) but keep regex on level parts inside parentheses.
			// New canonical options include descriptive names before parentheses (e.g. "Reflections (Open + Advanced)")
			const lower = pass.toLowerCase();
			const wantsOpen =
				(/(footprints|open)/i.test(pass) && /\(open/.test(pass)) ||
				/\bopen \+/i.test(pass) ||
				/resolutions|full pack/i.test(pass) ||
				/reflections/i.test(pass);
			const wantsAdvanced =
				(/(milestones|advanced)/i.test(pass) && /advanced/i.test(pass)) ||
				/\bopen \+ advanced\b/i.test(pass) ||
				/resolutions|full pack/i.test(pass) ||
				/reflections|recollections/i.test(pass);
			const wantsInvitational =
				(/(giant steps|invitational)/i.test(pass) && /invitational/i.test(pass)) ||
				/\bopen \+ invitational\b/i.test(pass) ||
				/resolutions|full pack/i.test(pass) ||
				/recollections/i.test(pass);
			if (wantsOpen) requiredRoleFields.push("role_Open");
			if (wantsAdvanced) requiredRoleFields.push("role_Advanced");
			if (wantsInvitational) requiredRoleFields.push("role_Invitational");
		} else if (schema.key === "registration-26-css") {
			const classes = form.classes || [];
			const hasOpen = classes.some((c) => /Lindy Hop\s+Footprints/i.test(c));
			const hasAdv = classes.some((c) => /Lindy Hop\s+Milestones/i.test(c));
			const hasInv = classes.some((c) => /Lindy Hop\s+Giant Steps/i.test(c));
			if (hasOpen) requiredRoleFields.push("role_Open");
			if (hasAdv) requiredRoleFields.push("role_Advanced");
			if (hasInv) requiredRoleFields.push("role_Invitational");
		}
		for (const section of schema.sections || []) {
			for (const f of section.fields) {
				const dynamicRequired = requiredRoleFields.includes(f.name);
				if (!f.required && !dynamicRequired) continue;
				const v = form[f.name];
				if (f.type === "multiSelect") {
					if (!Array.isArray(v) || v.length === 0)
						return `Please select at least one option for: ${f.label}`;
				} else if (f.type === "checkbox") {
					if (!v) return `Please confirm: ${f.label}`;
				} else if (typeof v === "string") {
					if (!v.trim()) return `Please fill: ${f.label}`;
				} else if (v == null) {
					return `Please fill: ${f.label}`;
				}
			}
		}
		return null;
	};

	const onSubmit = (e) => {
		e.preventDefault();
		const err = validate();
		if (err) {
			setStatus({ error: err });
			return;
		}
		// Ensure required standalone checkboxes (like payCashConsent) are validated
		if (schema?.standaloneFields) {
			for (const f of schema.standaloneFields) {
				if (f.required && f.type === "checkbox" && !form[f.name]) {
					setStatus({ error: `Please confirm: ${f.label}` });
					return;
				}
			}
		}
		setLoading(true);
		const headers = { "Content-Type": "application/json" };
		if (formKey === "registration-26-css" && cssAccessToken)
			headers["x-css-access"] = cssAccessToken;
		fetch(endpoint, {
			method: "POST",
			headers,
			body: JSON.stringify(form)
		})
			.then((r) => r.json().then((j) => ({ ok: r.ok, body: j })))
			.then(({ ok, body }) => {
				if (!ok) throw new Error(body.error || "Submission failed");
				const msg = body.copySent
					? "Submitted successfully – a copy has been emailed to you."
					: "Submitted successfully.";
				setStatus({ success: msg });
				setTimeout(() => {
					if (schema?.key === "registration-26" || schema?.key === "registration-26-css") {
						navigate("/festivalaki/registration-success", { replace: true });
					}
				}, 250);
			})
			.catch((e) => setStatus({ error: e.message || "Submission failed" }))
			.finally(() => setLoading(false));
	};

	const renderDescription = (desc, className) => {
		if (!desc) return null;
		const lines = (Array.isArray(desc) ? desc : [desc]).filter((l) => l != null);
		const isCss = schema?.key === "registration-26-css";
		const levelRegex = /^(FOOTPRINTS|MILESTONES|GIANT STEPS)\s*:\s*(.*)$/i;
		// Support either dash or colon pricing formats and optional EUR/€ suffix
		const pricingLineRegex = /^(.*?)\s*(?:\(([^)]+)\))?\s*(?:-|:)\s*(\d+)\s*(?:€|EUR)\s*$/i;
		const out = [];
		let collectingPricing = false;
		let pricingRows = [];
		const mapSubtitle = {
			FOOTPRINTS: "Open Level",
			MILESTONES: "Advanced Level",
			"GIANT STEPS": "Advanced+ Level"
		};
		function flushPricing() {
			if (pricingRows.length) {
				out.push(
					<div key={"pricing-group-" + out.length} className="pricing-items">
						{pricingRows}
					</div>
				);
				pricingRows = [];
			}
		}
		lines.forEach((line, i) => {
			const t = line.trim();
			const levelMatch = levelRegex.exec(t);
			if (levelMatch) {
				flushPricing();
				const label = levelMatch[1];
				const rest = levelMatch[2];
				const subtitle = isCss ? mapSubtitle[label.toUpperCase()] : null;
				if (isCss) {
					out.push(
						<div key={"lvl-" + i} className="level-block">
							<div className="level-pass-heading-wrap">
								<div className="level-pass-heading">{label.toLowerCase()}</div>
								{subtitle && (
									<div className="heading-subtitle">{subtitle}</div>
								)}
							</div>
							{rest && <p className="level-text">{rest}</p>}
						</div>
					);
				} else {
					// Show badges based on capacity with variants:
					//  - Level Sold Out (variant "level"): allFull
					//  - Followers Sold Out (variant "role"): followers full, leaders not
					//  - Leaders Sold Out (variant "role"): leaders full, followers not
					let badges = [];
					const capStatus = schema?.capacityStatus;
					if (capStatus) {
						const levelKey =
							label.toLowerCase().includes("footprints") || label.toLowerCase().includes("open")
								? "Open"
								: label.toLowerCase().includes("milestones") ||
								  label.toLowerCase().includes("advanced")
								? "Advanced"
								: "Invitational"; // internal key preserved
						const st = capStatus[levelKey];
						if (st) {
							if (st.allFull) badges.push({ label: "Level Sold Out", variant: "level" });
							else if (st.followersFull && !st.leadersFull)
								badges.push({ label: "Followers Sold Out", variant: "role" });
							else if (st.leadersFull && !st.followersFull)
								badges.push({ label: "Leaders Sold Out", variant: "role" });
						}
					}
					out.push(
						<div key={"lvl-" + i} className="level-block">
							<div className="level-heading">
								<span className="level-label">{displayLabel(label.toLowerCase())}</span>
								{badges.map((b) => (
									<span key={b.label} className={`capacity-badge capacity-badge--${b.variant}`}>
										{b.label}
									</span>
								))}
							</div>
							{rest && <p className="level-text">{rest}</p>}
						</div>
					);
				}
				return;
			}
			if (t === "---") {
				flushPricing();
				out.push(<hr key={"hr-" + i} className="desc-divider" />);
				return;
			}
			if (
				/^pricing:?$/i.test(t) ||
				(schema?.key === "registration-26" &&
					(/^combos:?$/i.test(t) || /^levels:?$/i.test(t) || /^level combos:?$/i.test(t) || /^(level pass|combo pass|full pass)$/i.test(t)))
			) {
				flushPricing();
				const headingText = t.replace(/:$/, "");
				const showPrice =
					/^levels$/i.test(headingText) || /^(level combos|combos)$/i.test(headingText) || /^(level pass|combo pass|full pass)$/i.test(headingText);
				const badgePrice = /^(level pass)$/i.test(headingText)
					? "50€"
					: /^(combo pass)$/i.test(headingText)
					? "80€"
					: /^(full pass)$/i.test(headingText)
					? "100€"
					: null;
				const subtitle = /^(level pass)$/i.test(headingText)
					? "(1.5h lindy hop + 1.5h solo jazz)"
					: /^(combo pass)$/i.test(headingText)
					? "(3h lindy hop + 3h solo jazz)"
					: /^(full pass)$/i.test(headingText)
					? "(4.5h lindy hop + 4.5h solo jazz)"
					: null;
				if (isCss && /^pricing$/i.test(headingText)) collectingPricing = true;
				out.push(
					<div key={"hdr-" + i} className="">
						{showPrice && badgePrice && (<div
							className={
								isCss && /^pricing$/i.test(headingText) ? "level-label" : "level-pass-heading"
							}
						>
							{headingText + " - " + badgePrice}
						</div>
						)}
						{subtitle && (
							<div className="heading-subtitle">{subtitle}</div>
						)}
					</div>
				);
				return;
			}
			if (isCss && collectingPricing) {
				const m = pricingLineRegex.exec(t);
				if (m) {
					const name = m[1].trim();
					const price = m[3] + "€";
					pricingRows.push(
						<div key={"price-" + i} className="pricing-item-row">
							<span className="pricing-item-name">{name}</span>
							<span className="price pricing-item-price">{price}</span>
							<hr key={"hr-" + i} className="pricing-desc-divider" />
						</div>
					);
					return;
				} else {
					collectingPricing = false;
					flushPricing();
				}
			}
			flushPricing();		
		});
		flushPricing();
		return out;
	};

	const renderField = (f) => {
		const commonClass = f.type === "longText" ? "input-field textarea-field" : "input-field";
		switch (f.type) {
			case "email":
				return (
					<input
						type="email"
						name={f.name}
						required={f.required}
						value={form[f.name] || ""}
						onChange={(e) => onChange(f.name, e.target.value, f.type)}
						className={commonClass}
					/>
				);
			case "shortText": {
				// Inline promo code + button
				if (f.name === "promoCode" && schema?.key === "registration-26") {
					const addDisabled = !form.promoCode || !form.promoCode.trim();
					return (
						<div className="promo-wrapper">
							<div className="promo-input-field">
								<input
									type="text"
									name={f.name}
									required={f.required}
									value={form[f.name] || ""}
									onChange={(e) => onChange(f.name, e.target.value, f.type)}
									className={`${commonClass}`}
									disabled={!!promoInfo}
								/>
								{promoInfo ? (
									<button
										type="button"
										className="promo-button"
										disabled
									>
										Applied
									</button>
								) : (
									<button
										type="button"
										className={`promo-button${addDisabled ? " is-disabled" : ""}`}
										onClick={validatePromoManually}
										disabled={addDisabled}
									>
										ADD
									</button>
								)}
							</div>
							{promoError && <p className="promo-error">{promoError}</p>}
							{/* promoSplitDetails handled by schema field below */}
						</div>
					);
				}
				return (
					<input
						type="text"
						name={f.name}
						required={f.required}
						value={form[f.name] || ""}
						onChange={(e) => onChange(f.name, e.target.value, f.type)}
						className={commonClass}
					/>
				);
			}
			case "longText":
				return (
					<textarea
						name={f.name}
						rows={4}
						value={form[f.name] || ""}
						required={f.required}
						onChange={(e) => onChange(f.name, e.target.value, f.type)}
						className={commonClass}
					/>
				);
			case "singleSelect": {
				// Capacity-based suspension applies ONLY to normal form
				const disabledDueToCapacity = (() => {
					if (schema?.key !== "registration-26") return false;
					if (!/^role_/.test(f.name) || !schema?.capacityStatus) return false;
					const levelKey = f.name.replace("role_", "");
					const status = schema.capacityStatus[levelKey];
					return !!status?.allFull;
				})();
				return (
					<div className="select-wrapper">
						<select
							name={f.name}
							required={f.required}
							value={form[f.name] || ""}
							onChange={(e) => onChange(f.name, e.target.value, f.type)}
							className={commonClass}
							disabled={disabledDueToCapacity}
						>
							<option value="" disabled>
								Select...
							</option>
							{(f.options || []).map((o) => {
								let disableOption = false;
								// Disable pass options (normal form only) if they include any fully sold out level
								if (
									schema?.key === "registration-26" &&
									f.name === "pass" &&
									schema.capacityStatus
								) {
									const lv = deriveLevels(o);
									const cs = schema.capacityStatus;
									if (
										(lv.open && cs.Open?.allFull) ||
										(lv.advanced && cs.Advanced?.allFull) ||
										(lv.invitational && cs.Invitational?.allFull)
									) {
										disableOption = true;
									}
								}
								// Promo-based pass restriction filtering
								if (schema?.key === "registration-26" && f.name === "pass" && promoInfo?.spec) {
									const passLabel = o.toLowerCase();
									const isFull = /resolutions|full pack/.test(passLabel);
									const isCombo =
										/reflections/.test(passLabel) ||
										/recollections/.test(passLabel) ||
										/(open \+ advanced|advanced \+ invitational)/.test(passLabel);
									const isSingle = !isFull && !isCombo;
									if (promoInfo.spec.restriction === "singleLevels" && !isSingle)
										disableOption = true;
									if (promoInfo.spec.restriction === "combos" && !isCombo) disableOption = true;
								}
								// Role option capacity disabling ONLY for normal form
								if (schema?.key === "registration-26" && /^role_/.test(f.name)) {
									const levelKey = f.name.replace("role_", "");
									const status = schema.capacityStatus?.[levelKey];
									if (status) {
										if (/^Leader$/i.test(o) && status.leadersFull) disableOption = true;
										if (/^Follower$/i.test(o) && status.followersFull) disableOption = true;
										if (status.allFull) disableOption = true;
									}
								}
								{
									return (
										<option key={o} value={o} disabled={disableOption}>
											{displayLabel(o)}
										</option>
									);
								}
							})}
						</select>
					</div>
				);
			}
			case "multiSelect": {
				const selected = form[f.name] || [];
				// Special two-column grouping for CSS form class selection
				const isCssClasses = schema?.key === "registration-26-css" && f.name === "classes";
				if (isCssClasses) {
					const lindy = (f.options || []).filter((o) => /^Lindy Hop/i.test(o));
					const jazz = (f.options || []).filter((o) => /^Authentic Jazz/i.test(o));
					const displayLabelLocal = (o) => {
						const base = o.replace(/^Lindy Hop\s+/i, "").replace(/^Authentic Jazz\s+/i, "");
						return base.replace(/\bInvitational\b/g, "Advanced+");
					};
					// Capacity-based disabling removed for CSS classes (always selectable)
					function classDisabled() {
						return false;
					}
					const renderCol = (title, list) => (
						<div className="class-column" key={title}>
							<p className="class-column-heading">{title}</p>
							<div className="class-column-items">
								{list.map((o) => {
									const checked = selected.includes(o);
									const disabled = classDisabled(o);
									return (
										<label key={o} className={`class-row${disabled ? " is-disabled" : ""}`}>
											<input
												type="checkbox"
												checked={checked}
												onChange={() => !disabled && onChange(f.name, o, "multiSelect")}
												disabled={disabled}
											/>
											<span>{displayLabelLocal(o)}</span>
										</label>
									);
								})}
							</div>
						</div>
					);
					return (
						<div className="checkbox-group">
							{renderCol("Lindy Hop", lindy)}
							{renderCol("Solo Jazz", jazz)}
						</div>
					);
				}
				return (
					<div className="checkbox-group">
						{(f.options || []).map((o) => {
							const checked = selected.includes(o);
							return (
								<label key={o} className="checkbox-wrapper">
									<input
										type="checkbox"
										checked={checked}
										onChange={() => onChange(f.name, o, "multiSelect")}
									/>
									<span>{o}</span>
								</label>
							);
						})}
					</div>
				);
			}
			case "checkbox":
				return (
					<label className="checkbox-wrapper">
						<input
							type="checkbox"
							name={f.name}
							checked={!!form[f.name]}
							required={f.required}
							onChange={(e) => onChange(f.name, e.target.checked, "checkbox")}
							className="checkbox-input"
						/>
						<span>
							{displayLabel(f.label)}
							{f.required && " *"}
						</span>
					</label>
				);
			default:
				return null;
		}
	};

	const standalone = schema?.standaloneFields || [];
	const submitDisabled =
		loading || standalone.some((f) => f.required && f.type === "checkbox" && !form[f.name]);

	return (
		<div className="form-page page-container">
			<img src="/festivalatsi.png" alt="decorative" className="festivalaki-hero" />
			<h3 className="event-dates-heading">4 - 6 December 2026</h3>
			<form onSubmit={onSubmit}>
				{schema?.sections?.map((section, i) => {
					const isMerchSection =
						/merch/i.test(section.title) || section.fields.some((fl) => merchImages[fl.name]);
					return (
						<fieldset
							key={i}
							className={
								`form-section$${""}`.replace("$", "") +
								(section.title && /pass selection/i.test(section.title)
									? " form-section--pass-selection"
									: "")
							}
						>
						<legend>{section.title}</legend>
						{section.description && renderDescription(section.description, "section-description")}
						{section.fields.map((f) => {
							// Inject dynamic extra field below promo code (DIY26XANTHI) for split details
							const isPromoSection = section.fields.some((fl) => fl.name === "promoCode");
							// Hide promoSplitDetails unless promo indicates
							if (f.name === "promoSplitDetails") {
								const appliedCode = promoInfo?.code || "";
								if (appliedCode !== "DIY26XANTHI") return null; // only show after successful XANTHI apply
							}
							// Dynamic visibility for per-level role selects
							if (schema?.key === "registration-26") {
								const pass = form.pass || "";
								if (!pass && ["role_Open", "role_Advanced", "role_Invitational"].includes(f.name))
									return null;
								if (pass) {
									const {
										open: wantsOpen,
										advanced: wantsAdvanced,
										invitational: wantsInvitational
									} = deriveLevels(pass);
									if (f.name === "role_Open" && !wantsOpen) return null;
									if (f.name === "role_Advanced" && !wantsAdvanced) return null;
									if (f.name === "role_Invitational" && !wantsInvitational) return null;
								}
							}
							if (schema?.key === "registration-26-css") {
								const classes = form.classes || [];
								// Hide all role selects until at least one Lindy class chosen
								const anyLindy = classes.some((c) => /^Lindy Hop/i.test(c));
								if (
									!anyLindy &&
									["role_Open", "role_Advanced", "role_Invitational"].includes(f.name)
								)
									return null;
								// For each level-specific role, only show if that Lindy class selected
								if (
									f.name === "role_Open" &&
									!classes.some((c) => /Lindy Hop\s+Footprints/i.test(c))
								)
									return null;
								if (
									f.name === "role_Advanced" &&
									!classes.some((c) => /Lindy Hop\s+Milestones/i.test(c))
								)
									return null;
								if (
									f.name === "role_Invitational" &&
									!classes.some((c) => /Lindy Hop\s+Giant Steps/i.test(c))
								)
									return null;
							}
							// Legacy roleOther field if still present (hide unless role === Other)
							if (
								f.name === "roleOther" &&
								![form.role_Open, form.role_Advanced, form.role_Invitational].some(
									(v) => v === "Other"
								)
							)
							return null;
							const isTshirtField = f.name === "tshirt_konto" || f.name === "tshirt_konto_f";
							return (
								<div
									key={f.name}
									className={`form-field${merchImages[f.name] ? " form-field--merch" : ""}${
										isTshirtField ? " form-field--merch--tshirt" : ""
									}`}
								>
									{/* Standard (non-merch) label above full row */}
									{!merchImages[f.name] && f.type !== "checkbox" && f.type !== "multiSelect" && (
										<label
											className={`field-label${
												f.name === "promoSplitDetails" ? " field-label--no-cap" : ""
											}`}
										>
											<span>
												{displayLabel(f.label)}
												{f.required && " *"}
											</span>
											{typeof f.price === "number" && (
												<span className="price">{f.price}€</span>
											)}
										</label>
									)}
									<div className="field-input-with-media">
										{merchImages[f.name] ? (
											<>
												<div className="merch-field-main">
													<label className="field-label">
														<span>
															{f.label}
															{f.required && " *"}
														</span>
														{typeof f.price === "number" && (
															<span className="price">{f.price}€</span>
														)}
													</label>
													{renderField(f)}
												</div>
												{/* If this is the Kontogiorgis tshirt, render front + back images */}
												{isTshirtField ? (
													<div className="merch-thumb-group">
														<div className="merch-thumb-wrap merch-thumb--tshirt">
															<img
																src={merchImages[f.name].src}
																alt={merchImages[f.name].alt}
																loading="lazy"
																decoding="async"
																className="merch-thumb merch-thumb--tshirt"
															/>
														</div>
														{/* <div className="merch-thumb-wrap merch-thumb--tshirt merch-thumb--back">
															<img
																src={merchImages.tshirt_konto_back.src}
																alt={merchImages.tshirt_konto_back.alt}
																loading="lazy"
																decoding="async"
																className="merch-thumb merch-thumb--tshirt merch-thumb--back"
															/>
														</div> */}
													</div>
												) : (
													<div className="merch-thumb-wrap">
														<img
															src={merchImages[f.name].src}
															alt={merchImages[f.name].alt}
															loading="lazy"
															decoding="async"
															className="merch-thumb"
														/>
													</div>
												)}
											</>
										) : (
											renderField(f)
										)}
									</div>
									{/* {f.type === "multiSelect" && <p className="field-hint">Select all that apply</p>} */}
									{/* Legacy dynamic roleOther support (kept for schemas that omit roleOther field) */}
									{f.name === "role" &&
										form.role === "Other" &&
										!section.fields.some((fl) => fl.name === "roleOther") && (
											<div className="other-role-wrapper">
												<label className="field-label">Specify other role</label>
												<input
													type="text"
													name="roleOther"
													value={form.roleOther || ""}
													onChange={(e) => onChange("roleOther", e.target.value, "shortText")}
													className="input-field"
												/>
											</div>
										)}
								</div>
							);
						})}
						{isMerchSection && (
							<>
								<div className="merch-thanks">
									Kontogiorgis/Kontogiorgaina designs by:{" "}
									<a href="https://www.instagram.com/mechitattt">Elena</a>
								</div>
								<div className="merch-thanks">
									DIY '26 tote design by:{" "}
									<a href="https://www.instagram.com/aliki.hirakaki/">Aliki</a>
								</div>
							</>
						)}
					</fieldset>
				);
			})}
				{/* Standalone fields (e.g., consent checkboxes) rendered just above submit */}
				{standalone.length > 0 && (
					<div className="confirmation_checkboxes">
						{standalone.map((f) => (
							<div key={f.name} className="form-field">
								{renderField(f)}
							</div>
						))}
					</div>
				)}
				<div className="submit-section">
					<button disabled={submitDisabled} type="submit" className="submit-button">
						{loading ? "Submitting..." : "SUBMIT"}
					</button>
					{submitDisabled && !loading && (
						<p className="submit-confirm">
							Please confirm required checkboxes above to enable submission.
						</p>
					)}
				</div>
			</form>
			{status?.error && <p className="status-error">{status.error}</p>}
			{status?.success && <p className="status-success">{status.success}</p>}
		</div>
	);
}
